/**
 * Shared cell chrome every column type reuses (spec M1-B5 §6, M2 §6).
 *
 * Nothing here knows a column type; the type folders compose these. Keeping them in one place
 * is what makes "status is centred, text is left, numbers are right" a one-line declaration in
 * each folder instead of thirteen copies of the same flex box.
 */
import { Icon, icons, statusColor } from '@elizmonday/ui';
import { createPortal } from 'react-dom';
import {
  Component,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

/* ── read-only chrome ─────────────────────────────────────────────────────── */

/**
 * Board search highlights every match inside the name cell and any text cell `[B4§8.9]`.
 * Case-insensitive, non-overlapping, no regex from user input (the term is escaped).
 */
export function Highlight({ text, term }: { text: string; term: string }) {
  if (!term.trim() || !text) return <>{text}</>;
  const needle = term.trim().toLowerCase();
  const haystack = text.toLowerCase();
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (;;) {
    const at = haystack.indexOf(needle, cursor);
    if (at < 0) break;
    if (at > cursor) parts.push(text.slice(cursor, at));
    parts.push(
      <mark key={`${at}`} className="ez-cell__mark">
        {text.slice(at, at + needle.length)}
      </mark>,
    );
    cursor = at + needle.length;
  }
  if (cursor === 0) return <>{text}</>;
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

export function CellText({
  text,
  align = 'left',
  search = '',
  title,
}: {
  text: string;
  align?: 'left' | 'center' | 'right';
  search?: string;
  title?: string;
}) {
  return (
    <span className="ez-cell__text" data-align={align} title={title ?? (text || undefined)}>
      <Highlight text={text} term={search} />
    </span>
  );
}

/** The empty-cell affordance: nothing at all, so the grid line is the only ink. */
export function EmptyCell() {
  return <span className="ez-cell__empty" aria-hidden="true" />;
}

/* ── editors ──────────────────────────────────────────────────────────────── */

/**
 * The single-line inline editor behind `text`, `numbers`, `name` and the add-item row.
 *
 * Measured on monday's sandbox board (`discovery/ui-reference/editors/<type>/
 * inline-behaviour.json`, captured by `tools/browser/capture-inline-editors.mjs`):
 *
 * | type | opened by | value shown | caret on open | Enter | Tab | Escape |
 * |---|---|---|---|---|---|---|
 * | `text` | 2nd click | the text | **at the end**, nothing selected | commit, leave | commit | revert |
 * | `numbers` | 2nd click | the **raw** number (`190.5`, not `$190.50`) | **all selected** | commit, leave | commit | revert |
 * | `name` | 2nd click / Enter | the name | **at the end** | commit, leave | commit | revert |
 *
 * So the caret rule is per type, not one rule: round 3 selected the whole value everywhere,
 * which is right for `numbers` (you retype a number) and wrong for `text` and `name` (you
 * append to a name). `caret` carries it.
 *
 * `reject` is `numbers`' "non-numeric input is refused as it is typed"
 * (`discovery/ui-reference/editors/README.md`): a keystroke that would produce a value the
 * type cannot hold never reaches the draft, so there is nothing to clean up on commit.
 */
export function InlineTextEditor({
  initial,
  initialInput,
  placeholder,
  align = 'left',
  inputMode,
  caret = 'end',
  accept,
  clearable = false,
  onCommit,
  onCancel,
  onNavigate,
  autoFocus = true,
  ariaLabel,
}: {
  initial: string;
  initialInput?: string;
  placeholder?: string;
  align?: 'left' | 'center' | 'right';
  inputMode?: 'text' | 'decimal';
  /** Where the caret lands when the editor opens (measured per type; see the table above). */
  caret?: 'end' | 'all';
  /** A draft this predicate refuses is discarded keystroke by keystroke (`numbers`). */
  accept?: (next: string) => boolean;
  /** monday puts an `×` inside the editing cell for `numbers`; it clears and commits. */
  clearable?: boolean;
  onCommit(next: string): void;
  onCancel(): void;
  onNavigate?(direction: 'up' | 'down' | 'left' | 'right' | 'next' | 'prev'): void;
  autoFocus?: boolean;
  ariaLabel?: string;
}) {
  // `initialInput` seeds the editor when it was opened by typing a printable key (ASSUMED, Q51).
  const [draft, setDraft] = useState(initialInput ?? initial);
  const ref = useRef<HTMLInputElement>(null);
  const committed = useRef(false);
  /** Has the person touched this draft? Once they have, it is theirs (see below). */
  const typed = useRef(initialInput !== undefined);
  const seeded = useRef(initial);

  /**
   * **P-277.** The row can change **under** an open editor, and the value on screen has to
   * follow it.
   *
   * `draft` was seeded once, at mount. So a `Ctrl+V` on a cell whose editor was open — the
   * grid's paste path writes it, `cells.setMany` answers `changed: true`, the toast says
   * *"Pasted 1 cell"* — left this input showing the value the cell held **before** the paste
   * (an empty `text`, a `numbers` with nothing but its `×`), for as long as the editor stayed
   * mounted. Worse than the wrong pixels: the input's own `blur` then committed that stale
   * draft **back over** the pasted value. The same held for every other external write —
   * another tab, an automation, a mass edit from another row in the selection.
   *
   * So while the draft is untouched it tracks `initial`, which is the row's value as the store
   * holds it. Once the person has typed, their draft is theirs: a write landing underneath does
   * not throw away what they are in the middle of writing (that is the case the undo stack and
   * `invalidateUndo` are for).
   */
  useEffect(() => {
    if (initial === seeded.current) return;
    seeded.current = initial;
    if (typed.current) return;
    setDraft(initial);
  }, [initial]);

  useLayoutEffect(() => {
    if (!autoFocus) return;
    const input = ref.current;
    if (!input) return;
    input.focus();
    // Typing to open always puts the caret after the seed, whatever the type's rule is.
    if (initialInput !== undefined) input.setSelectionRange(draft.length, draft.length);
    else if (caret === 'all') input.select();
    else input.setSelectionRange(input.value.length, input.value.length);
    // Only on mount: re-selecting on every keystroke would fight the caret.
  }, []);

  const commit = (value = draft) => {
    if (committed.current) return;
    committed.current = true;
    onCommit(value);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      committed.current = true;
      onCancel();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      commit();
      onNavigate?.('down');
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      commit();
      onNavigate?.(event.shiftKey ? 'prev' : 'next');
      return;
    }
    // Arrows inside a text editor move the caret, not the focus.
    event.stopPropagation();
  };

  const input = (
    <input
      ref={ref}
      className="ez-cell__input"
      // P-124: the coverage ledger counts elements by `data-testid`; without one the inline
      // editor behind `text` / `numbers` / `name` / `email` / `phone` cannot be counted at all.
      data-testid="cell-input"
      data-align={align}
      inputMode={inputMode}
      aria-label={ariaLabel}
      value={draft}
      placeholder={placeholder}
      onChange={(event) => {
        const next = event.target.value;
        // Paste goes through `onChange` too, so one predicate covers typing and pasting.
        if (accept && !accept(next)) return;
        typed.current = true;
        setDraft(next);
      }}
      onKeyDown={onKeyDown}
      onBlur={() => commit()}
    />
  );

  if (!clearable) return input;
  return (
    <span className="ez-cell__inline-editor">
      {input}
      <button
        type="button"
        className="ez-cell__inline-clear"
        aria-label="Clear"
        data-testid="inline-clear"
        // `mousedown` — a `click` would land after the input's `blur` has already committed.
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDraft('');
          commit('');
        }}
      >
        ×
      </button>
    </span>
  );
}

/* ── the draft rule every popover editor obeys (P-282) ────────────────────── */

/** A stable key for a draft, so "the store moved" is a comparison and not a re-render. */
function draftKey(draft: unknown): string {
  try {
    return JSON.stringify(draft) ?? 'undefined';
  } catch {
    return String(draft);
  }
}

/**
 * One editor's draft, as `useEditorDraft` hands it out.
 *
 * `touched` is the render snapshot (for rendering only); `dismiss` reads the live one, because
 * the outside click can land in the same tick as the keystroke that made the draft the
 * person's.
 */
export interface EditorDraft<TDraft> {
  readonly draft: TDraft;
  /** Has the person touched it? A render snapshot — `dismiss` decides with the live value. */
  readonly touched: boolean;
  /** Replace the draft. Every control the **person** operates goes through this. */
  set(next: TDraft): void;
  /** The same, as a recipe, for the pickers that toggle one entry of a list. */
  update(recipe: (current: TDraft) => TDraft): void;
  /**
   * The outside click. `commit` runs only when the draft is the person's; otherwise `abandon`
   * closes the editor and **writes nothing at all**.
   */
  dismiss(commit: () => void, abandon: () => void): void;
}

/**
 * **P-282 — the rule for every editor that seeds a draft at mount, popover or inline.**
 *
 * `InlineTextEditor` learnt it in round 10w (P-277) and the popovers were skipped, on the
 * reading that a popover renders into a portal at the end of `<body>` and so a paste inside one
 * never reaches the grid's `onPaste`. That is wrong: a React portal propagates its events
 * through the **React** tree, not the DOM tree, so the `paste` bubbles from the popover's field
 * to the scroller's `onPaste` exactly as it does from a cell — which `preventDefault()`s the
 * browser's own insert and routes the text through the store. The popover's fields were left
 * holding the draft they were seeded with, and the dismiss then committed that stale draft back
 * over the pasted value: the `update_column_value` with the url followed 1–2 s later by
 * `update_column_value` with `value: null` that P-282 was filed on.
 *
 * Two halves, and both are needed:
 *
 *  1. **While the draft is untouched it follows the store.** `fromValue` is recomputed from the
 *     `value` prop on every render — the row's value as the store holds it — so a paste, an
 *     automation, another tab or a mass edit landing underneath replaces what the open editor
 *     shows. Once the person has typed or picked, the draft is theirs and nothing overwrites it.
 *  2. **An untouched dismiss writes nothing.** Even with (1), an editor that commits its seed on
 *     every outside click writes a value nobody chose — `hour` wrote 09:00 onto an empty cell
 *     just by being opened and clicked away from — and turns every stray click into an activity
 *     row. `dismiss` sends the untouched case to `onCancel`, which closes the editor without a
 *     request.
 *
 * `seed` is the draft an editor **opened by typing a printable key** starts from (Q51); a seeded
 * draft counts as touched from the first frame, because the person typed it.
 */
export function useEditorDraft<TDraft>(fromValue: TDraft, seed?: TDraft): EditorDraft<TDraft> {
  const [draft, setDraft] = useState<TDraft>(seed === undefined ? fromValue : seed);
  const touched = useRef(seed !== undefined);
  const live = useRef(draft);
  live.current = draft;
  const store = useRef(fromValue);
  store.current = fromValue;
  const key = draftKey(fromValue);
  const followed = useRef(key);

  useEffect(() => {
    if (key === followed.current) return;
    followed.current = key;
    if (touched.current) return;
    live.current = store.current;
    setDraft(store.current);
  }, [key]);

  const update = useCallback((recipe: (current: TDraft) => TDraft) => {
    touched.current = true;
    setDraft((current) => {
      const next = recipe(current);
      live.current = next;
      return next;
    });
  }, []);

  const set = useCallback(
    (next: TDraft) => {
      update(() => next);
    },
    [update],
  );

  const dismiss = useCallback((commit: () => void, abandon: () => void) => {
    if (touched.current) commit();
    else abandon();
  }, []);

  return { draft, touched: touched.current, set, update, dismiss };
}

/** Gap between the cell and the popover, and the margin kept to the viewport edge. */
const POPOVER_GAP = 2;
const POPOVER_MARGIN = 8;

/**
 * The popover frame every picker editor sits in.
 *
 * **It renders into a portal at the end of `<body>`, exactly as monday does**
 * (`discovery/ui-reference/editors/README.md`: "the editor is a portal at the end of `<body>`").
 * That is not a stylistic choice — it is the fix for the defect that shipped in round 3: the
 * popover used to be an `position: absolute; top: 100%` child of the cell, and `.ez-cell` is
 * `overflow: hidden`, so **every picker was clipped to nothing**. The cell went blank on the
 * second click (the editor replaced the renderer) and no popover was ever visible, on the
 * fixture route and on a product board alike. Every editor Playwright test passed anyway,
 * because a clipped element still has a layout box and still matches `toBeVisible()`.
 *
 * Positioning is therefore `fixed`, measured from the owning `.ez-cell`, and it flips above /
 * shifts left rather than leaving the viewport. It closes on outside click and `Escape` is
 * handled by the editor `[B5§6.1 E6]`.
 */
export function CellPopover({
  children,
  onDismiss,
  onCancel,
  width,
  label,
  variant,
}: {
  children: ReactNode;
  /** The **outside click**: monday commits a picker by clicking away from it. */
  onDismiss(): void;
  /**
   * **P-121.** `Escape` *cancels* an editor `[B5§6.1 E6]`, and the frame used to route both
   * gestures to `onDismiss` — so the twelve pickers that (correctly) pass their commit as the
   * outside-click handler wrote the draft on `Escape` as well. The two gestures are two props;
   * a frame that has only one exit passes only `onDismiss` and nothing changes for it.
   */
  onCancel?(): void;
  width?: number;
  label: string;
  /** `data-variant`, for the few cards whose padding is not monday's default 8 px. */
  variant?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onDismiss();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        (onCancel ?? onDismiss)();
      }
    };
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [onDismiss, onCancel]);

  useLayoutEffect(() => {
    const place = () => {
      const cell = anchorRef.current?.closest('.ez-cell') ?? anchorRef.current?.parentElement;
      if (!cell) return;
      const anchor = cell.getBoundingClientRect();
      const box = ref.current?.getBoundingClientRect();
      const w = width ?? box?.width ?? 200;
      const h = box?.height ?? 0;
      let top = anchor.bottom + POPOVER_GAP;
      if (h > 0 && top + h > window.innerHeight - POPOVER_MARGIN) {
        // Flip above the cell when there is more room there; otherwise pin to the viewport.
        top = anchor.top - POPOVER_GAP - h >= POPOVER_MARGIN ? anchor.top - POPOVER_GAP - h : POPOVER_MARGIN;
      }
      const left = Math.max(
        POPOVER_MARGIN,
        Math.min(anchor.left, window.innerWidth - w - POPOVER_MARGIN),
      );
      setPosition((current) =>
        current && current.top === top && current.left === left ? current : { top, left },
      );
    };
    place();
    // The grid scrolls under the popover; re-anchor rather than letting it drift.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [width, children]);

  return (
    <>
      {/* Stays in the cell so the portal can find the cell it belongs to. */}
      <span ref={anchorRef} className="ez-cell-popover-anchor" aria-hidden="true" />
      {createPortal(
        <div
          ref={ref}
          className="ez-cell-popover"
          role="dialog"
          aria-label={label}
          data-variant={variant}
          data-testid="cell-popover"
          /*
           * No `visibility: hidden` while the anchor is being measured, however tempting:
           * a hidden element **cannot take focus**, so the editor inside it (the long-text
           * textarea, the people search field) called `focus()` into the void and the caret
           * stayed on the cell. `place()` runs in a layout effect, i.e. before the browser
           * paints, so the un-measured `0,0` frame is never on screen anyway.
           */
          style={{
            ...(width ? { width } : undefined),
            top: position?.top ?? 0,
            left: position?.left ?? 0,
          }}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}

/**
 * Above how many options a picker offers a search field at all (parity card `P-003`).
 *
 * monday shows the label buttons **immediately** for a short list and only adds a search box
 * once the list is long; the exact threshold is not captured, so 10 is **ASSUMED** and is
 * raised as an open question. Every picker reads it from here so one edit changes all of them.
 */
export const PICKER_SEARCH_THRESHOLD = 10;

/**
 * A footer row under a picker's options — monday's `✎ Edit Labels` and the AI row below the
 * separator `[P-003]`. Left-aligned icon + label, full width, 32 px.
 */
export function PickerRow({
  icon,
  label,
  onClick,
  disabled,
  title,
  testId,
  centered = false,
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  testId?: string;
  /** monday centres `Edit Labels` in a 112 px button rather than filling the row `[P-003]`. */
  centered?: boolean;
}) {
  return (
    <button
      type="button"
      className={centered ? 'ez-picker__row ez-picker__row--centered' : 'ez-picker__row'}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
    >
      <span className="ez-picker__row-icon" aria-hidden="true">
        {icon}
      </span>
      {label}
    </button>
  );
}

export function PickerSeparator() {
  return <hr className="ez-picker__separator" />;
}

/** A search field shared by the label, dropdown and people pickers. */
export function PickerSearch({
  value,
  onChange,
  placeholder,
  autoFocus = true,
  testId = 'picker-search',
}: {
  value: string;
  onChange(next: string): void;
  placeholder: string;
  autoFocus?: boolean;
  /** P-124: `picker-search` unless a picker needs to be told apart from another on screen. */
  testId?: string;
}) {
  return (
    <div className="ez-picker__search">
      <input
        autoFocus={autoFocus}
        className="ez-picker__search-input"
        type="search"
        data-testid={testId}
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => event.stopPropagation()}
      />
    </div>
  );
}

/* ── avatars, shared by `people`, `person` and presence ───────────────────── */

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function PersonAvatar({
  name,
  color,
  photoUrl,
  size = 26,
  title,
}: {
  name: string;
  color: string;
  photoUrl?: string | null;
  size?: number;
  title?: string;
}) {
  return (
    <span
      className="ez-avatar"
      style={{ width: size, height: size, backgroundColor: statusColor(color) }}
      title={title ?? name}
      data-testid="person-avatar"
    >
      {photoUrl ? <img src={photoUrl} alt="" width={size} height={size} /> : initialsOf(name)}
    </span>
  );
}

/** monday renders an outlined placeholder for an unknown or deleted person `[B5§8.1]`. */
export function UnknownAvatar({ size = 26 }: { size?: number }) {
  return (
    <span
      className="ez-avatar ez-avatar--unknown"
      style={{ width: size, height: size }}
      title="Unknown person"
    >
      <Icon icon={icons.person} size={Math.round(size * 0.6)} ignoreFocusStyle />
    </span>
  );
}

/* ── the editor error boundary ────────────────────────────────────────────── */

/** One crash, as `CellEditorBoundary` reports it. */
export interface EditorCrash {
  readonly columnId: string;
  readonly columnType: string;
  readonly message: string;
  readonly stack?: string;
}

/**
 * Every editor crash this page has seen, newest last — the verifier and the e2e suite read it,
 * and it is what "reports the error" means when there is no toast on the board screen yet.
 */
declare global {
  var __ezEditorCrashes: EditorCrash[] | undefined;
}

export function recordEditorCrash(crash: EditorCrash): void {
  const log = (globalThis.__ezEditorCrashes ??= []);
  log.push(crash);
  console.error(`Cell editor crashed (${crash.columnType} · ${crash.columnId}): ${crash.message}`);
  if (typeof document !== 'undefined') {
    document.dispatchEvent(new CustomEvent('ez:editor-crash', { detail: crash }));
  }
}

/**
 * **Defence in depth for round 5t: a crashing editor closes itself, it never takes the board
 * route with it.**
 *
 * The live defect was one bad line in the `mirror` editor (`columnIds.map` over the wrong
 * serialisation of `displayed_linked_columns`), but the *damage* was structural: an editor
 * renders inside the board route with nothing between them, so React unwound to the nearest
 * `errorElement` and `/__ui/board` became *Something went wrong · Retry* — the grid, the
 * selection and any unsaved neighbouring edit gone, for a cell the user merely clicked on.
 * The fix for that one line lives in `registry.ts`'s normaliser and in
 * `packages/shared/src/columns/mirror`; this is the guarantee that the *next* one costs a
 * closed popover instead of the screen.
 *
 * It has to be a class: `getDerivedStateFromError` / `componentDidCatch` have no hook form.
 * `onClose` runs in the commit phase, which is where React expects a boundary to schedule its
 * recovery, and the grid answers it by leaving edit mode — so the cell falls back to its
 * renderer rather than to an empty box. `resetKey` (the cell address) clears the failed state
 * when the boundary is reused for a different cell.
 */
export class CellEditorBoundary extends Component<
  {
    readonly children: ReactNode;
    readonly columnId: string;
    readonly columnType: string;
    readonly resetKey?: string;
    /** The grid stops editing; the crash is already recorded when this runs. */
    onClose(crash: EditorCrash): void;
  },
  { readonly failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidUpdate(previous: { resetKey?: string }): void {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }

  override componentDidCatch(error: unknown): void {
    const crash: EditorCrash = {
      columnId: this.props.columnId,
      columnType: this.props.columnType,
      message: error instanceof Error ? error.message : String(error),
      ...(error instanceof Error && error.stack ? { stack: error.stack } : {}),
    };
    recordEditorCrash(crash);
    this.props.onClose(crash);
  }

  override render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}
